import { Button, Modal, Table, Tooltip } from "antd";
import Ellipse from "components/Global/Ellipse";
import { FC, useEffect, useState } from "react";
import { capitalizeOnlyFirstLetter } from "utils/utils";

interface PackagingListModalProps {
  isVisible: boolean;
  packagingData: any[];
  onClose: () => void;
  onGeneratePaySlip: (packageId: string) => void;
  packageId: string;
  loadingPaySlip: Record<string, boolean>;
}

const { Column } = Table;

const PackagingListModal: FC<PackagingListModalProps> = ({
  isVisible,
  packagingData,
  onClose,
  onGeneratePaySlip,
  packageId,
  loadingPaySlip,
}) => {
  const [processedData, setProcessedData] = useState<any[]>([]);

  // Process data to identify groups of BOX type pallets with same dimensions and container type
  useEffect(() => {
    if (!packagingData?.length) return;

    // Create a deep copy of the packaging data
    const dataCopy = JSON.parse(JSON.stringify(packagingData));

    // Get unique pallet names while preserving order
    const uniquePalletNames: string[] = [];
    const palletMap: Record<string, number> = {}; // Map palletName to index

    dataCopy.forEach((pallet: any) => {
      if (!Object.prototype.hasOwnProperty.call(palletMap, pallet.palletName)) {
        palletMap[pallet.palletName] = uniquePalletNames.length;
        uniquePalletNames.push(pallet.palletName);
      }
    });

    // Create an object to track box groups by pallet + container type
    const groups: Record<string, any[]> = {};

    // Counter for box ranges within each pallet
    const palletBoxCounters: Record<string, Record<string, number>> = {};

    // First pass: group by pallet + container type and assign box ranges
    dataCopy.forEach((pallet: any, index: number) => {
      // Add pallet sequence number and check if it's a mix pallet
      const palletSequence = palletMap[pallet.palletName] + 1;
      const isMixPallet =
        pallet.palletType && pallet.palletType.toUpperCase() === "MIXPALLET";

      // Create the display name for the pallet
      pallet.displayPalletName = isMixPallet
        ? `Pallet ${palletSequence} (MIX)`
        : `Pallet ${palletSequence}`;

      // Handle BOX type pallets
      if (pallet.containerType === "BOX") {
        const palletName = pallet.palletName;
        const containerTypeName = pallet.containerTypeName;

        // Initialize counters for this pallet if not exists
        if (!palletBoxCounters[palletName]) {
          palletBoxCounters[palletName] = {};
        }

        // Initialize counter for this container type within this pallet
        if (!palletBoxCounters[palletName][containerTypeName]) {
          palletBoxCounters[palletName][containerTypeName] = 1;
        }

        // Assign box range number within this pallet
        pallet.boxRangeNumber =
          palletBoxCounters[palletName][containerTypeName];
        palletBoxCounters[palletName][containerTypeName]++;

        // Create group key: pallet + container type (for spanning rows with same container in same pallet)
        const groupKey = `${palletName}_${containerTypeName}`;

        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }

        groups[groupKey].push({ ...pallet, originalIndex: index });
      }
    });

    // Second pass: add metadata for grouping
    const processedPallets = dataCopy.map((pallet: any, index: number) => {
      if (pallet.containerType === "BOX") {
        const groupKey = `${pallet.palletName}_${pallet.containerTypeName}`;
        const group = groups[groupKey];

        // Find position within the group
        const groupIndex = group.findIndex(
          (item) => item.originalIndex === index
        );

        return {
          ...pallet,
          groupKey: groupKey,
          groupIndex,
          groupSize: group.length,
        };
      }

      return pallet;
    });

    setProcessedData(processedPallets);
  }, [packagingData]);

  // Helper function to calculate box ranges
  const calculateBoxRange = (record: any) => {
    // Group all items by pallet name
    const groupedData = processedData.reduce((acc, curr) => {
      if (!acc[curr.palletName]) acc[curr.palletName] = [];
      acc[curr.palletName].push(curr);
      return acc;
    }, {});

    const currentGroup = groupedData[record.palletName];

    // Find the index of the current record in its group
    const currentIndex = currentGroup.findIndex(
      (item) => item.id === record.id
    );

    // Initialize box counter for this pallet starting at 1
    let boxStart = 1;

    // If not the first item in the pallet, calculate based on previous items in same pallet
    if (currentIndex > 0) {
      const previousItems = currentGroup.slice(0, currentIndex);

      // Calculate the starting box number based on previous items in this pallet
      previousItems.forEach((item) => {
        if (item.containerType === "BOX") {
          // For BOX type, increment by just 1 (regardless of how many items in the same BOX group)
          // If this is a grouped BOX, we only increment once for the whole group
          const isPartOfGroup = item.groupSize > 1;
          const isFirstInGroup = item.groupIndex === 0;

          if (!isPartOfGroup || (isPartOfGroup && isFirstInGroup)) {
            boxStart += 1;
          }
        } else {
          // For ITEM or MASTERCARTON, use the totalQuantity
          boxStart += item.quantity || 0;
        }
      });
    }

    // Calculate the end range based on container type
    let boxEnd;
    if (record.containerType === "BOX") {
      // For BOX type, show the same number for start and end (e.g., "1-1")
      boxEnd = boxStart;
    } else {
      // For other container types, add the totalQuantity to get the end range
      boxEnd = boxStart + (record.quantity || 0) - 1;
    }

    // For zero quantity, show dash
    if (!record.quantity) return "-";

    // Always return as a range format
    return `${boxStart}-${boxEnd}`;
  };

  // Helper function to calculate vertical margin for centering content in rowspan cells
  const calculateVerticalMargin = (rowSpanCount: number) => {
    if (rowSpanCount === 1) return 0;
    if (rowSpanCount === 2) return 6;
    if (rowSpanCount === 3) return 12;
    return (rowSpanCount - 1) * 6;
  };

  // Helper function to check if current row should show pallet dimensions
  const shouldShowPalletDimensions = (record: any, index: number) => {
    // Check if this is the first occurrence of this pallet name
    const isFirstOccurrence =
      index === 0 || processedData[index - 1].palletName !== record.palletName;

    if (isFirstOccurrence) {
      // Calculate how many consecutive rows have the same pallet name and same dimensions
      let rowSpanCount = 1;
      const currentDimensions = record.palletDimensions;
      const currentDimensionsString = `${
        currentDimensions?.palletLength || "0"
      } x ${currentDimensions?.palletWidth || "0"} x ${
        currentDimensions?.palletHeight || "0"
      }`;

      for (let i = index + 1; i < processedData.length; i++) {
        const nextRecord = processedData[i];
        if (nextRecord.palletName !== record.palletName) break;

        const nextDimensions = nextRecord.palletDimensions;
        const nextDimensionsString = `${
          nextDimensions?.palletLength || "0"
        } x ${nextDimensions?.palletWidth || "0"} x ${
          nextDimensions?.palletHeight || "0"
        }`;

        if (nextDimensionsString === currentDimensionsString) {
          rowSpanCount++;
        } else {
          break;
        }
      }

      return rowSpanCount;
    }

    return 0;
  };

  // Helper function to check if current row should show pallet weight
  const shouldShowPalletWeight = (record: any, index: number) => {
    // Check if this is the first occurrence of this pallet name
    const isFirstOccurrence =
      index === 0 || processedData[index - 1].palletName !== record.palletName;

    if (isFirstOccurrence) {
      // Calculate how many consecutive rows have the same pallet name and same weight
      let rowSpanCount = 1;
      const currentWeight = record.palletDimensions?.palletWeight;

      for (let i = index + 1; i < processedData.length; i++) {
        const nextRecord = processedData[i];
        if (nextRecord.palletName !== record.palletName) break;

        const nextWeight = nextRecord.palletDimensions?.palletWeight;

        if (nextWeight === currentWeight) {
          rowSpanCount++;
        } else {
          break;
        }
      }

      return rowSpanCount;
    }

    return 0;
  };

  return (
    <Modal
      title="Packaging Slip"
      open={isVisible}
      onCancel={onClose}
      footer={[
        <Button
          key="generate"
          type="primary"
          size="large"
          onClick={() => onGeneratePaySlip(packageId)}
          loading={!!loadingPaySlip[packageId]}
          style={{ marginTop: "20px" }}
        >
          Generate packing slip
        </Button>,
      ]}
      width="80%"
    >
      <Table
        dataSource={processedData}
        rowKey="id"
        pagination={false}
        scroll={{ y: "calc(100vh - 390px)" }}
        className="table-global-packing-slip"
      >
        <Column
          title="Pallet"
          dataIndex="displayPalletName"
          key="displayPalletName"
          className="bg-white"
          width={120}
          render={(text, record, index) => {
            console.log("sadasd", text);
            // Check if this is the first occurrence of this pallet name
            const isFirstOccurrence =
              index === 0 ||
              processedData[index - 1].palletName !== record.palletName;

            if (isFirstOccurrence) {
              // Calculate how many consecutive rows have the same pallet name
              const rowSpanCount = processedData
                .slice(index)
                .findIndex(
                  (item, i) => i > 0 && item.palletName !== record.palletName
                );

              const actualRowSpan =
                rowSpanCount === -1
                  ? processedData.length - index
                  : rowSpanCount;

              return {
                children: text,
                props: {
                  rowSpan: actualRowSpan,
                },
              };
            }

            // For subsequent rows with the same pallet name, hide the cell
            return {
              children: text,
              props: {
                rowSpan: 0,
              },
            };
          }}
        />

        <Column
          title={
            <Tooltip title="L x W x H" destroyTooltipOnHide={true}>
              <span>Pallet Dimensions</span>
            </Tooltip>
          }
          key="palletDimensions"
          render={(text, record, index) => {
            const rowSpan = shouldShowPalletDimensions(record, index);
            const { palletLength, palletWidth, palletHeight } =
              record.palletDimensions || {};
            const dimensionsText = `${palletLength || "0"} x ${
              palletWidth || "0"
            } x ${palletHeight || "0"}`;

            if (rowSpan > 0) {
              return {
                children: dimensionsText,
                props: {
                  rowSpan: rowSpan,
                },
              };
            } else if (rowSpan === 0) {
              return {
                children: dimensionsText,
                props: {
                  rowSpan: 0,
                },
              };
            }

            return dimensionsText;
          }}
          className="bg-white"
          width={150}
        />

        <Column
          title="Pallet Weight"
          dataIndex="palletDimensions"
          key="palletWeight"
          
          className="bg-white"
          width={110}
          render={(text, record, index) => {
            const rowSpan = shouldShowPalletWeight(record, index);
            const weightText = record?.palletDimensions?.palletWeight;

            if (rowSpan > 0) {
              return {
                children: weightText,
                props: {
                  rowSpan: rowSpan,
                },
              };
            } else if (rowSpan === 0) {
              return {
                children: weightText,
                props: {
                  rowSpan: 0,
                },
              };
            }

            return weightText;
          }}
        />

        <Column
          title="Box Range"
          key="boxRange"
          render={(text, record) => {
            // For BOX containers that are part of a group, only show the range once
            if (record.containerType === "BOX" && record.groupSize > 1) {
              // Only show box range for the first item in the group
              if (record.groupIndex === 0) {
                const verticalMargin = calculateVerticalMargin(record.groupSize);
                return {
                  children: (
                    <div style={{ marginTop: `${verticalMargin}px` }}>
                      {calculateBoxRange(record)}
                    </div>
                  ),
                  props: {
                    rowSpan: record.groupSize,
                  },
                };
              }
              return {
                props: {
                  rowSpan: 0,
                },
              };
            }

            // For non-grouped items, show as normal
            return calculateBoxRange(record);
          }}
          className="bg-white"
          width={110}
        />

        <Column
          title="Item"
          dataIndex="itemName"
          key="itemName"
          className="bg-white"
          width={140}
          render={(text, record) => (
            <Ellipse
              message={record?.itemName || "-"}
              maxLength={20}
              isTooltip={true}
              tooltipMessage={record?.itemName || "-"}
            />
          )}
        />

        <Column
          title="Model"
          dataIndex="modelName"
          key="modelName"
          className="bg-white"
          width={140}
          render={(text, record) => (
            <Ellipse
              message={record?.modelName || "-"}
              maxLength={20}
              isTooltip={true}
              tooltipMessage={record?.modelName || "-"}
            />
          )}
        />

        <Column
          title="Qty"
          dataIndex="quantity"
          key="quantity"
          render={(text, record) => record?.quantity || "-"}
          className="bg-white"
          width={70}
        />

        <Column
          title="Qty Per Box"
          dataIndex="quantityPerBox"
          key="qtyPerBox"
          render={(text, record) => record?.quantityPerBox || "-"}
          className="bg-white"
          width={100}
        />

        <Column
          title="Total Quantity"
          key="totalQuantity"
          render={(text, record) => {
            return record.totalQuantity;
          }}
          className="bg-white"
          width={110}
        />

        <Column
          title="Container Type"
          dataIndex="containerType"
          key="containerType"
          render={(text, record) => {
            // For BOX containers that are part of a group, only show the container type once
            if (record.containerType === "BOX" && record.groupSize > 1) {
              // Only show container type for the first item in the group
              if (record.groupIndex === 0) {
                const verticalMargin = calculateVerticalMargin(record.groupSize);
                return {
                  children: (
                    <div style={{ marginTop: `${verticalMargin}px` }}>
                      {capitalizeOnlyFirstLetter(record?.containerTypeName)}
                    </div>
                  ),
                  props: {
                    rowSpan: record.groupSize,
                  },
                };
              }
              return {
                props: {
                  rowSpan: 0,
                },
              };
            }

            // For non-grouped items, show as normal
            return record?.containerType === "BOX"
              ? capitalizeOnlyFirstLetter(record?.containerTypeName)
              : capitalizeOnlyFirstLetter(record?.containerType);
          }}
          className="bg-white"
          width={125}
        />

        <Column
          title={
            <Tooltip title="L x W x H" destroyTooltipOnHide={true}>
              <span>Box Dimensions</span>
            </Tooltip>
          }
          width={140}
          render={(text, record) => {
            // For BOX containers that are part of a group, only show dimensions once
            if (record.containerType === "BOX" && record.groupSize > 1) {
              // Only show dimensions for the first item in the group
              if (record.groupIndex === 0) {
                const { length, width, height } = record;
                const verticalMargin = calculateVerticalMargin(record.groupSize);
                return {
                  children: (
                    <div style={{ marginTop: `${verticalMargin}px` }}>
                      {`${length || "0"} x ${width || "0"} x ${height || "0"}`}
                    </div>
                  ),
                  props: {
                    rowSpan: record.groupSize,
                  },
                };
              }
              return {
                props: {
                  rowSpan: 0,
                },
              };
            }

            // For non-grouped items, show as normal
            const { length, width, height } = record;
            return `${length || "0"} x ${width || "0"} x ${height || "0"}`;
          }}
          className="bg-white"
        />

        <Column
          title="Box Weight"
          render={(text, record) => {
            // For BOX containers that are part of a group, only show weight once
            if (record.containerType === "BOX" && record.groupSize > 1) {
              // Only show weight for the first item in the group
              if (record.groupIndex === 0) {
                const verticalMargin = calculateVerticalMargin(record.groupSize);
                return {
                  children: (
                    <div style={{ marginTop: `${verticalMargin}px` }}>
                      {record?.weight?.toFixed(2) || "-"}
                    </div>
                  ),
                  props: {
                    rowSpan: record.groupSize,
                  },
                };
              }
              return {
                props: {
                  rowSpan: 0,
                },
              };
            }

            // For non-grouped items, show as normal
            return record?.weight?.toFixed(2) || "-";
          }}
          className="bg-white"
          width={100}
        />
      </Table>
    </Modal>
  );
};

export default PackagingListModal;
